import * as React from 'react';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { MainListItems, SecondaryListItems } from './menu/items';
import { UserSection } from './menu/userSection';
import LoadingComponent from './common/loading';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import PermissionGuard from './common/components/PermissionGuard';

const DashboardComponent = React.lazy(() => import('../dashboard/index'));
const InventoryComponent = React.lazy(() => import('../inventory/index'));
const ClientsComponent = React.lazy(() => import('../clients/index'));
const MattersComponent = React.lazy(() => import('../matters/index'));
const FoldersComponent = React.lazy(() => import('../folders/index'));
const SettingsComponent = React.lazy(() => import('../settings/index'));
const BarcodesComponent = React.lazy(() => import('../barcodes/index'));
const MaintenanceComponent = React.lazy(() => import('../maintenance/index'));
// ScannerComponent (../scanner/index) intentionally not routed — see Docs/pre-production-checklist.md.
// Its data model (client industry, matter billing/priority, document-category folders) doesn't
// correspond to anything FastTrack's real API tracks; it was built against mock data and never
// connected. Left in the tree in case a real version of this concept is built later.
const ReportsComponent = React.lazy(() => import('../reports/index'));
const UsersComponent = React.lazy(() => import('../users/index'));

const LocationsComponent = React.lazy(() => import('../maintenance/locations/index'));
const UserGroupsComponent = React.lazy(() => import('../maintenance/usergroups/index'));
const OfficesComponent = React.lazy(() => import('../maintenance/offices/index'));
const MediaComponent = React.lazy(() => import('../maintenance/media/index'));
const FileStatusComponent = React.lazy(() => import('../maintenance/filestatus/index'));
const EventsComponent = React.lazy(() => import('../maintenance/events/index'));
const DepartmentsComponent = React.lazy(() => import('../maintenance/departments/index'));
const RequestersComponent = React.lazy(() => import('../maintenance/requesters/index'));
const TypeoflawComponent = React.lazy(() => import('../maintenance/typeoflaw/index'));
const StdfoldersubfolderComponent = React.lazy(() => import('../maintenance/stdfoldersubfolder/index'));
const ActivityhistoryComponent = React.lazy(() => import('../activityhistory/index'));
const RetentionModule = React.lazy(() => import('../retention/index'));

const InventoryStatusReport = React.lazy(() => import('../reports/components/InventoryStatusReport'));
const CustomInventoryReport = React.lazy(() => import('../reports/components/CustomInventoryReport'));
const UserActivityReport = React.lazy(() => import('../reports/components/UserActivityReport'));
const ClientMatterSummaryReport = React.lazy(() => import('../reports/components/ClientMatterSummaryReport'));
const LocationUtilizationReport = React.lazy(() => import('../reports/components/LocationUtilizationReport'));
const RetentionAnalysisReport = React.lazy(() => import('../reports/components/RetentionAnalysisReport'));
const BarcodeAuditReport = React.lazy(() => import('../reports/components/BarcodeAuditReport'));
const TrackingHistoryReport = React.lazy(() => import('../reports/components/TrackingHistoryReport'));

const FileRequestsComponent = React.lazy(() => import('../file-requests/index'));

const InventoryMultiPanelComponent = React.lazy(() => import('../inventory/InventoryMultiPanelLayout/index'));

const drawerWidth = 240;

// const AppBar = styled(MuiAppBar, {
//   shouldForwardProp: (prop) => prop !== 'open',
// })(({ theme, open }) => ({
//   zIndex: theme.zIndex.drawer + 1,
//   transition: theme.transitions.create(['width', 'margin'], {
//     easing: theme.transitions.easing.sharp,
//     duration: theme.transitions.duration.leavingScreen,
//   }),
//   ...(open && {
//     marginLeft: drawerWidth,
//     width: `calc(100% - ${drawerWidth}px)`,
//     transition: theme.transitions.create(['width', 'margin'], {
//       easing: theme.transitions.easing.sharp,
//       duration: theme.transitions.duration.enteringScreen,
//     }),
//   }),
// }));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      backgroundColor: '#222',
      color: '#f0f0f0',
      fontWeight: 'bold',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(7),
        },
      }),
    },
  }),
);

const mdTheme = createTheme();

function CoreStructure() {

  const location = useLocation();
  const isInventoryPage = location.pathname.startsWith('/inventory');

  const [open, setOpen] = React.useState(!isInventoryPage);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  React.useEffect(() => {
    if (isInventoryPage) {
      setOpen(false);
    }
  }, [isInventoryPage]);

  return (
    // I would love to get rid of this. Its required for the MUI Datepicker and needs to be wrapped around 
    // all components that would use the Datepicker component.
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ThemeProvider theme={mdTheme}>
        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
          <CssBaseline />
          <Drawer variant="permanent" open={open}>
            <Toolbar
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                px: [1]
              }}
            >
              <h2 style={{ color: '#fff', textAlign: 'left', display: 'inilne' }}><em>Fast</em><span style={{ color: '#9e2828' }}>Track</span></h2>
              <IconButton onClick={toggleDrawer} sx={{ color: '#999', marginLeft: '30px' }}>
                <MenuOpenIcon />
              </IconButton>
            </Toolbar>
            <Divider sx={{ backgroundColor: '#000', boxShadow: '0px 2px 5px #000, 0px -2px 5px #000' }} />

            {/* Main content container with flex layout */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100vh - 64px)', // Use viewport height instead of percentage
              overflow: 'auto' // Prevent this container from scrolling
            }}>
              {/* Main menu items that will flex and scroll if needed */}
              <Box sx={{
                flexGrow: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                maxHeight: '100%',
                minHeight: 0,
                // Custom scrollbar styling
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#1a1a1a',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#444',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}>
                <List component="nav">
                  <MainListItems drawerOpen={open} />
                  <Divider sx={{ backgroundColor: '#000', boxShadow: '0px 2px 5px #000, 0px -2px 5px #000', my: 1 }} />
                  <SecondaryListItems drawerOpen={open} />
                </List>
              </Box>

              {/* User section that sticks to the bottom */}
              <Box sx={{
                marginTop: 'auto',
                width: '100%' // Ensure this is 100%
              }}>
                <List component="nav">
                  <UserSection open={open} />
                </List>
              </Box>
            </Box>
          </Drawer>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              height: '100vh',
              overflow: 'hidden', // Changed from 'auto' to 'hidden'
            }}
          >

            {/* <Toolbar /> */}

            <Container
              maxWidth={true}
              sx={{
                height: '100%', // Changed from calc(100vh - 64px) to 100%
                px: isInventoryPage ? '10px !important' : 3,
                mx: '0 !important',
                overflowY: 'auto',
                overflowX: 'hidden'
              }}
            >
              <Routes>
                <Route path="/" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <DashboardComponent />
                  </React.Suspense>
                }
                />
                {/* Update Inventory routes */}
                <Route path="/inventory" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <InventoryComponent />
                  </React.Suspense>
                } />
                {/* Inventory with selected client */}
                <Route path="/inventory/clients/:clientId" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <InventoryComponent />
                  </React.Suspense>
                } />

                {/* Inventory with selected client and matter */}
                <Route path="/inventory/clients/:clientId/matters/:matterId" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <InventoryComponent />
                  </React.Suspense>
                } />
                {/* Add this route for testing the new multi-panel layout */}
                <Route path="/inventory/multi-panel" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <InventoryMultiPanelComponent />
                  </React.Suspense>
                } />
                <Route path="/inventory/add" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <InventoryComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/inventory/edit/:id" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <InventoryComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                {/* Inventory with pre-selected client */}
                <Route path="/clients/:clientId/inventory/add" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <InventoryComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />

                {/* Inventory with pre-selected client and matter */}
                <Route path="/clients/:clientId/matters/:matterId/inventory/add" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <InventoryComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                {/* Inventory edit - client context */}
                <Route path="/clients/:clientId/inventory/edit/:id" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <InventoryComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />

                {/* Inventory edit - client/matter context */}
                <Route path="/clients/:clientId/matters/:matterId/inventory/edit/:id" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <InventoryComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                {/* Clients routes - Enhanced */}
                <Route path="/clients" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <ClientsComponent />
                  </React.Suspense>
                } />
                <Route path="/clients/add" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <ClientsComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/clients/edit/:id" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <ClientsComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />

                {/* Client-specific matters routes */}
                <Route path="/clients/:clientId/matters" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <MattersComponent parentType="client" />
                  </React.Suspense>
                } />
                <Route path="/clients/:clientId/matters/add" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <MattersComponent parentType="client" formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/clients/:clientId/matters/edit/:id" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <MattersComponent parentType="client" formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />

                {/* Standalone matters routes */}
                <Route path="/matters" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <MattersComponent />
                  </React.Suspense>
                } />
                <Route path="/matters/add" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <MattersComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/matters/edit/:id" element={
                  <PermissionGuard permission="canModifyData">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <MattersComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/folders/:parentId" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <FoldersComponent />
                  </React.Suspense>
                }
                />
                <Route path="/file-requests/*" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <FileRequestsComponent />
                  </React.Suspense>
                } />
                <Route path="/retention/*" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <RetentionModule />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/reports" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <ReportsComponent />
                  </React.Suspense>
                }
                />
                <Route path="/reports/custom-inventory" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <CustomInventoryReport />
                  </React.Suspense>
                } />
                <Route path="/reports/inventory-status" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <InventoryStatusReport />
                  </React.Suspense>
                } />

                <Route path="/reports/user-activity" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <UserActivityReport />
                  </React.Suspense>
                } />

                <Route path="/reports/client-matter-summary" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <ClientMatterSummaryReport />
                  </React.Suspense>
                } />
                <Route path="/reports/location-utilization" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <LocationUtilizationReport />
                  </React.Suspense>
                } />
                <Route path="/reports/retention-analysis" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <RetentionAnalysisReport />
                  </React.Suspense>
                } />
                <Route path="/reports/barcode-audit" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <BarcodeAuditReport />
                  </React.Suspense>
                } />
                <Route path="/reports/tracking-history" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <TrackingHistoryReport />
                  </React.Suspense>
                } />
                <Route path="/barcodes" element={
                  <React.Suspense fallback={<LoadingComponent />}>
                    <BarcodesComponent />
                  </React.Suspense>
                }
                />
                <Route path="/settings/*" element={
                  <PermissionGuard permission="settings">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <SettingsComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <MaintenanceComponent />
                    </React.Suspense>
                  </PermissionGuard>
                }
                />
                <Route path="/users" element={
                  <PermissionGuard permission="admin">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <UsersComponent />
                    </React.Suspense>
                  </PermissionGuard>
                }
                />
                <Route path="/users/add" element={
                  <PermissionGuard permission="admin">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <UsersComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                }
                />
                <Route path="/users/edit/:id" element={
                  <PermissionGuard permission="admin">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <UsersComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                }
                />

                {/* Maintenance sub-routes */}
                <Route path="/maintenance/locations" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <LocationsComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/locations/add" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <LocationsComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/locations/edit/:id" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <LocationsComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />

                <Route path="/maintenance/usergroups" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <UserGroupsComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/usergroups/add" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <UserGroupsComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/usergroups/edit/:id" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <UserGroupsComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />

                <Route path="/maintenance/offices" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <OfficesComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/offices/add" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <OfficesComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/offices/edit/:id" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <OfficesComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />

                <Route path="/maintenance/media" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <MediaComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/media/add" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <MediaComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/media/edit/:id" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <MediaComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />

                <Route path="/maintenance/filestatus" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <FileStatusComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/filestatus/add" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <FileStatusComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/filestatus/edit/:id" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <FileStatusComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />

                <Route path="/maintenance/events" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <EventsComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/events/add" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <EventsComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/events/edit/:id" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <EventsComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />

                <Route path="/maintenance/departments" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <DepartmentsComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/departments/add" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <DepartmentsComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/departments/edit/:id" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <DepartmentsComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/requesters" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <RequestersComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/requesters/add" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <RequestersComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/requesters/edit/:id" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <RequestersComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/typeoflaw" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <TypeoflawComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/typeoflaw/add" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <TypeoflawComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/typeoflaw/edit/:id" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <TypeoflawComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/stdfoldersubfolder" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <StdfoldersubfolderComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/stdfoldersubfolder/add" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <StdfoldersubfolderComponent formMode="add" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/maintenance/stdfoldersubfolder/edit/:id" element={
                  <PermissionGuard permission="maintenance">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <StdfoldersubfolderComponent formMode="edit" />
                    </React.Suspense>
                  </PermissionGuard>
                } />
                <Route path="/activityhistory" element={
                  <PermissionGuard permission="transactionLog">
                    <React.Suspense fallback={<LoadingComponent />}>
                      <ActivityhistoryComponent />
                    </React.Suspense>
                  </PermissionGuard>
                } />
              </Routes>
            </Container>
          </Box>
        </Box>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default function Core() {
  return <CoreStructure />;
}